import { objectEntries, objectKeys } from "app/helpers/objects";
import { splitSignalsDTO } from "app/pages/sales-intelligence/domain/signals/utils/signals-dto-adapter";
import {
    isDraftSignalJsonObject,
    isEnrichedFiltersContentJsonObject,
    isEnrichedSignalsContentJsonObject,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/types/content.guards";
import type {
    LeadsSearchFilterKey,
    LeadsSearchFilterValue,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { filterTrackingRegistry } from "app/pages/sales-intelligence/modules/leads-search-filters/services/tracking/formatters";
import type Signal from "app/pages/sales-intelligence/sub-modules/signals/models/core/Signal";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";

import type { PocessedMessageData } from "../../store/types";
import type { EnrichedContentJsonObject, JsonWrapperName } from "../../types/content";

import { ValueChangeActionType } from "./types";

type FilterDetail = {
    filterName: LeadsSearchFilterKey;
    filterValue: string;
    isSignal: boolean;
};

type FilterChangeEntry = FilterDetail & {
    changeType: ValueChangeActionType;
};

const SELECT_JSON_WRAPPER_NAMES: ReadonlySet<JsonWrapperName> = new Set([
    "FiltersWereAdded",
    "SignalsFiltersWereAdded",
    "CraftedSignal",
]);

const MODIFY_JSON_WRAPPER_NAMES: ReadonlySet<JsonWrapperName> = new Set([
    "FiltersWereModified",
    "SignalsFiltersWereModified",
]);

const REMOVE_JSON_WRAPPER_NAMES: ReadonlySet<JsonWrapperName> = new Set([
    "FiltersWereRemoved",
    "SignalsFiltersWereRemoved",
]);

const isValidFilterKey = (key: string): key is LeadsSearchFilterKey => key !== "searchText";

const getChangeTypeFromWrapperName = (wrapperName: JsonWrapperName): ValueChangeActionType | null => {
    if (SELECT_JSON_WRAPPER_NAMES.has(wrapperName)) {
        return ValueChangeActionType.Select;
    }

    if (MODIFY_JSON_WRAPPER_NAMES.has(wrapperName)) {
        return ValueChangeActionType.Modify;
    }

    if (REMOVE_JSON_WRAPPER_NAMES.has(wrapperName)) {
        return ValueChangeActionType.Remove;
    }

    return null;
};

const extractFilterDetailsFromWrapper = (jsonObject: EnrichedContentJsonObject): readonly FilterDetail[] | null => {
    const isDraftSignal = isDraftSignalJsonObject(jsonObject);
    const isEnrichedSignalData = isEnrichedSignalsContentJsonObject(jsonObject);

    if (isDraftSignal || isEnrichedSignalData) {
        const signalsDTO = isDraftSignal
            ? signalsService.createSignalsDTOsGroupedByType([jsonObject.data as Signal])
            : jsonObject.data.signalsFilters;

        const signalsByTypeKey = splitSignalsDTO(signalsDTO);

        return objectKeys(signalsByTypeKey).reduce<FilterDetail[]>((acc, typeKey) => {
            if (signalsByTypeKey[typeKey].length === 0) {
                return acc;
            }

            const formattedValue = filterTrackingRegistry.format(typeKey, signalsByTypeKey[typeKey]);

            if (!formattedValue) {
                return acc;
            }

            return [...acc, { filterName: typeKey, filterValue: formattedValue, isSignal: true }];
        }, []);
    }

    if (isEnrichedFiltersContentJsonObject(jsonObject)) {
        return objectKeys(jsonObject.data).reduce((acc, filterCategory) => {
            const filtersList = jsonObject.data[filterCategory];

            // `salesforceCustomFilters` is an array (SI-8738), not a keyed object — skip it in this
            // field-keyed tracking projection (CRM tracking, if needed, would use a dedicated formatter).
            if (!filtersList || Array.isArray(filtersList)) {
                return acc;
            }

            // `Array.isArray` does not narrow a `readonly` array out of the union; the guard above already
            // excluded the array-shaped `salesforceCustomFilters` at runtime, so this keyed access is safe.
            return objectEntries(filtersList as Record<string, unknown>).reduce<FilterDetail[]>(
                (innerAcc, [filterKey, filterValue]) => {
                    const typedFilterKey = filterKey as LeadsSearchFilterKey;

                    if (!isValidFilterKey(typedFilterKey)) {
                        return innerAcc;
                    }

                    return [
                        ...innerAcc,
                        {
                            filterName: typedFilterKey,
                            filterValue: filterTrackingRegistry.format(
                                typedFilterKey,
                                filterValue as LeadsSearchFilterValue,
                            ),
                            isSignal: false,
                        },
                    ];
                },
                acc,
            );
        }, []);
    }

    return null;
};

export const getFilterChangesList = ({ jsonObjects }: PocessedMessageData): readonly FilterChangeEntry[] => {
    return jsonObjects.reduce<FilterChangeEntry[]>((acc, object) => {
        if (object.metadata.dataSource === "user") {
            return acc;
        }

        const changeType = getChangeTypeFromWrapperName(object.name);
        if (!changeType) {
            return acc;
        }

        const filterDetails = extractFilterDetailsFromWrapper(object);
        if (!filterDetails) {
            return acc;
        }

        return [
            ...acc,
            ...filterDetails.map((detail) => ({
                changeType,
                ...detail,
            })),
        ];
    }, []);
};
